// <OrgTable> — Org View Step 6 (T-052, spec §6.4).
//
// Alternate dense rendering of the same employees the card grid shows.
// Same data, denser layout. Click anywhere on the row → opens the
// profile (matches spec §6.4 last paragraph). The actions cell stops
// click propagation so opening the menu doesn't simultaneously open
// the profile.
//
// Sortable columns (Name / Team / Start date) are wired in T-054 pixel
// polish along with the matching Sort dropdown — for now header rows
// render the spec's labels but don't carry any click handler.

import { computeInitials } from './OrgEmployeeCard';
import { OrgEmployeeCardMenu, type OrgViewerRole } from './OrgEmployeeCardMenu';
import { ArrowDown, ArrowUp, MoreVertical } from 'lucide-react';
import type { OrgViewEmployee } from '@pa/shared';
import type { OrgSortKey } from './OrgSortDropdown';

export interface OrgTableProps {
  employees: OrgViewEmployee[];
  viewerRole: OrgViewerRole;
  onOpenProfile: (id: string) => void;
  onQuickEdit: (id: string) => void;
  onSendMessage: (employee: OrgViewEmployee) => void;
  onDeactivate: (employee: OrgViewEmployee) => void;
  /** Spec §6.4 — sortable headers reflect the current sort selection
   *  and clicking a sortable column sets the sort. */
  sort?: OrgSortKey;
  onSortChange?: (next: OrgSortKey) => void;
}

const STATUS_DOT_BG: Record<OrgViewEmployee['status'], string> = {
  active: 'bg-emerald-500',
  inactive: 'bg-slate-300',
};

const STATUS_LABEL: Record<OrgViewEmployee['status'], string> = {
  active: 'Active',
  inactive: 'Inactive',
};

export function OrgTable({
  employees,
  viewerRole,
  onOpenProfile,
  onQuickEdit,
  onSendMessage,
  onDeactivate,
  sort,
  onSortChange,
}: OrgTableProps) {
  return (
    <div
      data-testid="org-table"
      className="bg-white rounded-3xl border border-slate-200 shadow-sm overflow-hidden"
    >
      <table className="w-full text-sm">
        <thead className="bg-slate-50 border-b border-slate-100">
          <tr>
            <SortableTh
              sortKey="alpha"
              label="Name"
              activeSort={sort}
              onSortChange={onSortChange}
            />
            <Th>Title</Th>
            <SortableTh
              sortKey="team"
              label="Team"
              activeSort={sort}
              onSortChange={onSortChange}
            />
            <Th>Location</Th>
            <SortableTh
              sortKey="startDate"
              label="Start date"
              activeSort={sort}
              onSortChange={onSortChange}
            />
            <Th>Status</Th>
            <Th srLabel="Actions" align="right" />
          </tr>
        </thead>
        <tbody>
          {employees.map((emp) => (
            <Row
              key={emp.id}
              employee={emp}
              viewerRole={viewerRole}
              onOpenProfile={onOpenProfile}
              onQuickEdit={onQuickEdit}
              onSendMessage={onSendMessage}
              onDeactivate={onDeactivate}
            />
          ))}
        </tbody>
      </table>
    </div>
  );
}

function Th({
  children,
  srLabel,
  align = 'left',
}: {
  children?: React.ReactNode;
  srLabel?: string;
  align?: 'left' | 'right';
}) {
  return (
    <th
      scope="col"
      className={`text-${align} px-6 py-4 text-xs font-bold text-slate-400 uppercase tracking-wider`}
    >
      {srLabel ? <span className="sr-only">{srLabel}</span> : children}
    </th>
  );
}

function SortableTh({
  sortKey,
  label,
  activeSort,
  onSortChange,
}: {
  sortKey: OrgSortKey;
  label: string;
  activeSort?: OrgSortKey;
  onSortChange?: (next: OrgSortKey) => void;
}) {
  const isActive = activeSort === sortKey;
  // No callback wired? Render a normal Th — the column is "sortable in
  // theory" but the parent isn't wiring sort, so don't pretend.
  if (!onSortChange) {
    return <Th>{label}</Th>;
  }
  // The two non-default sorts get an explicit "newest first" / "A→Z"
  // direction icon that doesn't toggle (clicking the same key again
  // is a no-op per spec §6.1.1 — direction is implied by the option).
  const showDirectionIcon = isActive;
  const Icon = sortKey === 'startDate' ? ArrowDown : ArrowUp;
  return (
    <th
      scope="col"
      className="text-left px-6 py-4 text-xs font-bold text-slate-400 uppercase tracking-wider"
      aria-sort={isActive ? 'ascending' : 'none'}
    >
      <button
        type="button"
        onClick={() => onSortChange(sortKey)}
        data-testid={`org-table-sort-${sortKey}`}
        data-active={isActive}
        className={`inline-flex items-center gap-1 hover:text-slate-700 transition-colors ${
          isActive ? 'text-[#6344C6]' : ''
        }`}
      >
        <span>{label}</span>
        {showDirectionIcon ? (
          <Icon size={12} aria-hidden />
        ) : null}
      </button>
    </th>
  );
}

function Row({
  employee,
  viewerRole,
  onOpenProfile,
  onQuickEdit,
  onSendMessage,
  onDeactivate,
}: {
  employee: OrgViewEmployee;
  viewerRole: OrgViewerRole;
  onOpenProfile: (id: string) => void;
  onQuickEdit: (id: string) => void;
  onSendMessage: (employee: OrgViewEmployee) => void;
  onDeactivate: (employee: OrgViewEmployee) => void;
}) {
  function onRowKey(e: React.KeyboardEvent<HTMLTableRowElement>) {
    if (e.key === 'Enter' || e.key === ' ') {
      e.preventDefault();
      onOpenProfile(employee.id);
    }
  }
  return (
    <tr
      role="link"
      tabIndex={0}
      onClick={() => onOpenProfile(employee.id)}
      onKeyDown={onRowKey}
      data-testid={`org-table-row-${employee.id}`}
      className="border-b border-slate-50 last:border-0 hover:bg-slate-50/50 cursor-pointer focus:outline-none focus-visible:ring-2 focus-visible:ring-[#6344C6]/40"
    >
      <td className="px-6 py-3">
        <div className="flex items-center gap-3 min-w-0">
          <RowAvatar
            name={employee.name}
            avatarColor={employee.avatarColor}
            avatarUrl={employee.avatarUrl}
          />
          <span className="font-bold text-slate-800 truncate">
            {employee.name}
          </span>
        </div>
      </td>
      <td className="px-6 py-3 text-slate-600">{employee.title ?? '—'}</td>
      <td className="px-6 py-3 text-slate-600">{employee.teamName ?? '—'}</td>
      <td className="px-6 py-3 text-slate-600">{employee.siteName ?? '—'}</td>
      <td className="px-6 py-3 text-slate-600">
        {formatStartDate(employee.startDate)}
      </td>
      <td className="px-6 py-3">
        <span
          className="inline-flex items-center gap-1.5"
          data-testid={`org-table-status-${employee.id}`}
        >
          <span
            aria-hidden
            className={`w-2 h-2 rounded-full ${STATUS_DOT_BG[employee.status]}`}
          />
          <span className="text-[10px] font-bold text-slate-400 tracking-wider uppercase">
            {STATUS_LABEL[employee.status]}
          </span>
        </span>
      </td>
      <td
        className="px-6 py-3 text-right"
        onClick={(e) => e.stopPropagation()}
      >
        <OrgEmployeeCardMenu
          employee={employee}
          viewerRole={viewerRole}
          onOpenProfile={onOpenProfile}
          onQuickEdit={onQuickEdit}
          onSendMessage={onSendMessage}
          onDeactivate={onDeactivate}
        >
          {(triggerProps) => (
            <button
              type="button"
              {...triggerProps}
              data-testid={`org-table-menu-trigger-${employee.id}`}
              aria-label={`Open menu for ${employee.name}`}
              className="p-1 text-slate-400 hover:text-slate-600 focus:outline-none focus-visible:ring-2 focus-visible:ring-[#6344C6]/40 rounded"
            >
              <MoreVertical size={18} aria-hidden />
            </button>
          )}
        </OrgEmployeeCardMenu>
      </td>
    </tr>
  );
}

function RowAvatar({
  name,
  avatarColor,
  avatarUrl,
}: {
  name: string;
  avatarColor: string;
  avatarUrl: string | null;
}) {
  if (avatarUrl) {
    return (
      <span className="w-8 h-8 rounded-full overflow-hidden flex-shrink-0">
        <img src={avatarUrl} alt="" className="w-full h-full object-cover" />
      </span>
    );
  }
  return (
    <span
      className={`w-8 h-8 rounded-full flex items-center justify-center text-white text-xs font-bold flex-shrink-0 ${avatarColor}`}
      data-testid="org-table-avatar-fallback"
    >
      {computeInitials(name)}
    </span>
  );
}

const MONTHS_SHORT = [
  'Jan',
  'Feb',
  'Mar',
  'Apr',
  'May',
  'Jun',
  'Jul',
  'Aug',
  'Sep',
  'Oct',
  'Nov',
  'Dec',
];

function formatStartDate(iso: string): string {
  const m = iso.match(/^(\d{4})-(\d{2})-(\d{2})$/);
  if (!m) return iso;
  const monthIdx = Number(m[2]) - 1;
  const day = Number(m[3]);
  if (monthIdx < 0 || monthIdx > 11) return iso;
  return `${MONTHS_SHORT[monthIdx]} ${day}, ${m[1]}`;
}
